/**
 * useSequentialDataLoader - Последовательная загрузка данных при старте приложения
 * ЗАЧЕМ: Оптимизация порядка API запросов для минимизации времени загрузки
 * 
 * Порядок загрузки (по приоритету):
 * 1. market-status - статус рынка (нужен для UI индикаторов)
 * 2. all-contracts - список контрактов (нужен для селектора)
 * 3. expirations - даты экспирации (нужны после выбора контракта)
 * 4. strikes - страйки (нужны после выбора даты)
 * 5. quotes (WebSocket) - котировки (запускаем ПОСЛЕДНИМ)
 * 
 * Принцип: Каждый следующий запрос начинается ПОСЛЕ завершения предыдущего
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../config/api';

const API_KEY = 'XoMSGltU1i5Co0eoO0LMYbDY8YM4_2WLYXrCUNxsdQU';

// Глобальный кеш для предотвращения повторных запросов
let globalCache = {
  marketStatus: null,
  allContracts: null,
  marketStatusTimestamp: null,
  isReady: false, // Флаг готовности sequential loader
};

const MARKET_STATUS_TTL = 60000; // 1 минута

export const useSequentialDataLoader = () => {
  const [marketStatus, setMarketStatus] = useState(globalCache.marketStatus);
  const [allContracts, setAllContracts] = useState(globalCache.allContracts);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingStep, setLoadingStep] = useState('idle'); // idle | market-status | all-contracts | done
  const [error, setError] = useState(null);
  
  // Ref для защиты от дублирования (React StrictMode)
  const loadedRef = useRef(false);

  // Загрузка market-status
  const loadMarketStatus = useCallback(async () => {
    // Проверяем кеш
    const now = Date.now();
    if (globalCache.marketStatus && globalCache.marketStatusTimestamp && 
        (now - globalCache.marketStatusTimestamp) < MARKET_STATUS_TTL) {
      setMarketStatus(globalCache.marketStatus);
      return globalCache.marketStatus;
    }

    setLoadingStep('market-status');
    console.log('🔄 [Sequential] 1/2 Загружаем market-status...');
    const startTime = performance.now();

    try {
      const response = await fetch(`${API_BASE_URL}/api/databento/market-status`, {
        headers: { 'X-API-Key': API_KEY }
      });
      const data = await response.json();
      const duration = (performance.now() - startTime).toFixed(0);
      console.log(`✅ [Sequential] market-status загружен за ${duration}ms`);
      
      globalCache.marketStatus = data;
      globalCache.marketStatusTimestamp = Date.now();
      setMarketStatus(data);
      return data;
    } catch (err) {
      console.error('❌ [Sequential] Ошибка загрузки market-status:', err);
      throw err;
    }
  }, []);

  // Загрузка all-contracts
  const loadAllContracts = useCallback(async () => {
    // Проверяем кеш
    if (globalCache.allContracts) {
      setAllContracts(globalCache.allContracts);
      return globalCache.allContracts;
    }

    setLoadingStep('all-contracts');
    console.log('🔄 [Sequential] 2/2 Загружаем all-contracts...');
    const startTime = performance.now();

    try {
      const response = await fetch(`${API_BASE_URL}/api/databento/futures/all-contracts`, {
        headers: { 'X-API-Key': API_KEY }
      });
      const data = await response.json();
      const duration = (performance.now() - startTime).toFixed(0);
      
      if (data.status === 'success') {
        console.log(`✅ [Sequential] all-contracts загружен за ${duration}ms (${data.total_count} контрактов)`);
        globalCache.allContracts = data.contracts;
        setAllContracts(data.contracts);
        return data.contracts;
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (err) {
      console.error('❌ [Sequential] Ошибка загрузки all-contracts:', err);
      throw err;
    }
  }, []);

  // Последовательная загрузка
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    const loadSequentially = async () => {
      const totalStart = performance.now();
      console.log('🚀 [Sequential] Начинаем последовательную загрузку...');

      try {
        // ШАГ 1: market-status
        await loadMarketStatus();

        // ШАГ 2: all-contracts
        await loadAllContracts();

        const totalDuration = (performance.now() - totalStart).toFixed(0);
        console.log(`🎉 [Sequential] Загрузка завершена за ${totalDuration}ms`);
        
        globalCache.isReady = true; // Помечаем как готовый
        setLoadingStep('done');
        setIsLoading(false);
      } catch (err) {
        console.error('❌ [Sequential] Ошибка последовательной загрузки:', err);
        setError(err.message);
        setIsLoading(false);
      }
    };

    loadSequentially();
  }, [loadMarketStatus, loadAllContracts]);

  return {
    marketStatus,
    allContracts,
    isLoading,
    loadingStep,
    error
  };
};

// Функция для проверки готовности sequential loader
// ЗАЧЕМ: Компоненты могут проверить готовность перед запросами expirations/strikes
export const isSequentialLoaderReady = () => globalCache.isReady;

// Функция для ожидания готовности sequential loader
// ЗАЧЕМ: Позволяет отложить запросы до завершения критичных загрузок
export const waitForSequentialLoader = (maxWaitMs = 5000) => {
  return new Promise((resolve) => {
    if (globalCache.isReady) {
      resolve(true);
      return;
    }
    
    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (globalCache.isReady) {
        clearInterval(checkInterval);
        resolve(true);
      } else if (Date.now() - startTime > maxWaitMs) {
        clearInterval(checkInterval);
        console.warn('⚠️ [Sequential] Timeout waiting for loader');
        resolve(false);
      }
    }, 50);
  });
};

// Функция для сброса кеша (при необходимости)
export const resetSequentialCache = () => {
  globalCache = {
    marketStatus: null,
    allContracts: null,
    marketStatusTimestamp: null,
    isReady: false,
  };
};
